import React, { useEffect, useState } from "react";
import CustomTable from "../../../components/Tables/CustomTable";
import { MoreVertical, Grid, List } from "lucide-react";
import DropdownMenu from "../../ui/DropdownMenu";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import moment from "moment";
import { cleanObject } from "../../../helpers";
import {
  fetchAllProductsAsync,
  fetchApprovedProductsAsync,
  fetchPendingProductsAsync,
  fetchRejectedProductsAsync,
  setProductPage,
  setProductPageLimit,
} from "../../../redux/features/ProductSlice";

const ProductCard = ({ product, onViewDetails }: any) => {
  const firstImage = product.photos?.[0] || null;
  return (
    <div className="bg-white shadow rounded-md overflow-hidden flex flex-col">
      <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
        {firstImage ? (
          <img
            src={firstImage}
            alt={product.name}
            className="object-cover w-full h-full"
          />
        ) : (
          <span className="text-gray-400">No Image</span>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1 justify-between">
        <div>
          <h3 className="text-lg font-semibold">{product.name}</h3>
          <p className="text-sm text-gray-600 mt-1">
            {product.categoryIds?.title ?? "Uncategorized"}
          </p>
          <p className="text-sm text-gray-800 mt-2">
            {product.currencyId?.symbol} {product.price}
          </p>
        </div>
        <div className="mt-3 flex justify-between items-center">
          <span
            className={`px-2 py-1 rounded-full text-xs font-semibold ${
              product.approvalStatus === "APPROVED"
                ? "bg-green-100 text-green-700"
                : product.approvalStatus === "PENDING"
                ? "bg-yellow-100 text-yellow-700"
                : "bg-red-100 text-red-700"
            }`}
          >
            {product.approvalStatus}
          </span>
          <button
            className="text-indigo-600 text-sm underline"
            onClick={() => onViewDetails(product.id)}
          >
            Details
          </button>
        </div>
      </div>
    </div>
  );
};

const ProductsTable = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<"table" | "grid">("table");

  const {
    pendingProducts,
    rejectedProducts,
    loading,
    allProducts,
    approvedProducts,
    productPage,
    productPageLimit,
    status,
    search,
    dateRange,
    categoryId,
    vendorId,
  } = useAppSelector((state) => state.products);

  const products =
    status === "PENDING"
      ? pendingProducts?.items
      : status === "REJECTED"
      ? rejectedProducts?.items
      : status === "APPROVED"
      ? approvedProducts?.items
      : allProducts?.items;

  const totalPages =
    status === "PENDING"
      ? pendingProducts?.meta?.totalPages
      : status === "REJECTED"
      ? rejectedProducts?.meta?.totalPages
      : status === "ALL"
      ? allProducts?.meta?.totalPages
      : approvedProducts?.meta?.totalPages;

  const columns = [
    { label: "S/N", field: "index" },
    { label: "Name", field: "name" },
    { label: "Category", field: "category" },
    { label: "Vendor", field: "vendor" },
    { label: "Price", field: "price" },
    { label: "Status", field: "status" },
    { label: "Created At", field: "createdAt" },
    { label: "Action", field: "action" },
  ];

  const dropdownOptions = [
    {
      label: "View details",
      onClick: (id: string) => {
        navigate(`/products/details/${id}`);
      },
      returnBy: "id",
    },
  ];

  const rows = products?.map((product: any, index: number) => ({
    index: productPage * productPageLimit + (index + 1),
    name: product?.name,
    category: product?.categoryIds?.title,
    vendor: product?.vendorId?.fullName,
    price: `${product?.currencyId?.symbol} ${product?.price}`,
    status: (
      <span
        className={`px-3 py-1 rounded-full text-xs font-semibold ${
          product?.approvalStatus === "APPROVED"
            ? "bg-green-100 text-green-700"
            : product?.approvalStatus === "PENDING"
            ? "bg-yellow-100 text-yellow-700"
            : product?.approvalStatus === "REJECTED"
            ? "bg-red-100 text-red-700"
            : "bg-gray-100 text-gray-700"
        }`}
      >
        {product?.approvalStatus}
      </span>
    ),
    createdAt: moment(product?.createdAt).format("Do MMM YYYY"),
    action: (
      <DropdownMenu
        options={dropdownOptions}
        icon={<MoreVertical style={{ cursor: "pointer" }} />}
        item={product}
      />
    ),
  }));

  useEffect(() => {
    const params = cleanObject({
      limit: productPageLimit,
      page: productPage + 1,
      search,
      startDate: dateRange?.startDate,
      endDate: dateRange?.endDate,
      category: categoryId === "ALL" ? null : categoryId,
      vendorId: vendorId === "ALL" ? null : vendorId,
    });

    if (status === "ALL") {
      dispatch(fetchAllProductsAsync(params));
    } else if (status === "APPROVED") {
      dispatch(fetchApprovedProductsAsync(params));
    } else if (status === "PENDING") {
      dispatch(fetchPendingProductsAsync(params));
    } else {
      dispatch(fetchRejectedProductsAsync(params));
    }
  }, [
    dispatch,
    status,
    productPage,
    productPageLimit,
    dateRange,
    search,
    categoryId,
    vendorId,
  ]);

  return (
    <div>
      {/* Toggle Buttons */}
      <div className="flex justify-end mb-4 gap-2">
        <button
          className={`p-2 rounded ${
            viewMode === "table" ? "bg-indigo-600 text-white" : "bg-gray-200"
          }`}
          onClick={() => setViewMode("table")}
        >
          <List size={18} />
        </button>
        <button
          className={`p-2 rounded ${
            viewMode === "grid" ? "bg-indigo-600 text-white" : "bg-gray-200"
          }`}
          onClick={() => setViewMode("grid")}
        >
          <Grid size={18} />
        </button>
      </div>

      {viewMode === "table" ? (
        <CustomTable
          data={{ columns, rows }}
          noRecordText={`No ${status} Products`}
          loading={loading}
          showFooter={true}
          totalPages={totalPages}
          itemsPerPage={productPageLimit}
          currentPage={productPage}
          onPageChange={(val: number) => dispatch(setProductPage(val))}
          onLimitChange={(val: number) => {
            dispatch(setProductPageLimit(val));
            dispatch(setProductPage(0));
          }}
        />
      ) : (
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {products?.map((product: any) => (
              <ProductCard
                key={product.id}
                product={product}
                onViewDetails={(id: string) =>
                  navigate(`/products/details/${id}`)
                }
              />
            ))}
          </div>

          {/* Grid Pagination */}
          {products?.length > 0 && (
            <div className="flex justify-end mt-6 gap-2">
              <button
                className="px-3 py-1 border rounded disabled:opacity-50"
                disabled={productPage === 0}
                onClick={() => dispatch(setProductPage(productPage - 1))}
              >
                Prev
              </button>
              <span className="px-3 py-1 border rounded">
                {productPage + 1} / {totalPages}
              </span>
              <button
                className="px-3 py-1 border rounded disabled:opacity-50"
                disabled={productPage + 1 >= totalPages}
                onClick={() => dispatch(setProductPage(productPage + 1))}
              >
                Next
              </button>

              {/* Optional: items per page */}
              <select
                className="border rounded px-2 py-1 ml-2"
                value={productPageLimit}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  dispatch(setProductPageLimit(val));
                  dispatch(setProductPage(0));
                }}
              >
                {[10, 20, 50].map((v) => (
                  <option key={v} value={v}>
                    {v} / page
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductsTable;
